import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import './ClientProgram.css';
import { useNavigate } from 'react-router-dom';
import { encodeQueryParam } from '../../utils/urlUtils';

const ClientProgram = ({ programs, totalClients, selectedUserId }) => {
  const navigate = useNavigate();

  // Early return if no data is available yet
  if (!programs && totalClients === undefined) {
    return (
      <Box className="cp-container">
        <Typography variant="h6" className="cp-title" sx={{ fontWeight: 700 }}>
          Client Programs
        </Typography>
        <Box className="cp-inner-box" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Loading programs...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Function to generate a unique color based on the program string
  const generateColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Generate an HSL color for better color distribution
    const hue = Math.abs(hash) % 360;
    const saturation = 65 + (Math.abs(hash) % 25); 
    const lightness = 45 + (Math.abs(hash) % 15);
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Alternative function using more dynamic predefined colors
  const generateDynamicColor = (index, total) => {
    const baseColors = [
      '#F54BB1', '#F4C107', '#03BC72', '#572AF1', '#FF7A00', 
      '#E91E63', '#9C27B0', '#3F51B5', '#009688', '#FF5722',
      '#2196F3', '#4CAF50', '#FF9800', '#9E9E9E', '#607D8B',
      '#795548', '#FFC107', '#8BC34A', '#00BCD4', '#673AB7'
    ];
    
    if (index < baseColors.length) {
      return baseColors[index];
    }
    
    // For more programs, generate colors dynamically
    const hue = (index * 137.508) % 360;
    const saturation = 65 + (index % 3) * 10;
    const lightness = 50 + (index % 2) * 10;
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Ensure programs is an array before using array methods
  const programsArray = Array.isArray(programs) ? programs : [];

  const displayTotalClients = totalClients !== undefined 
    ? totalClients 
    : (programsArray.reduce((sum, program) => sum + program.clientcount, 0) || 0);

  const pieData = programsArray.slice(0, 10).map((program, index) => ({
    id: program.id,
    name: program.description,
    value: program.clientcount,
    color: generateDynamicColor(index, programsArray.length),
  }));

  const listData = programsArray.map((program, index) => ({
    id: program.id,
    name: program.description,
    value: program.clientcount,
    color: generateDynamicColor(index, programsArray.length),
  }));

  const handleFilterClick = (programName) => {
    if (!programName) return;
    const formattedProgramName = encodeQueryParam(programName);
    const userIdParam = selectedUserId ? `&userId=${selectedUserId}` : '';
    navigate(`/clients?program=${formattedProgramName}${userIdParam}`);
  };

  return (
    <Box className="cp-container">
      <Typography variant="h6" className="cp-title" sx={{ fontWeight: 700 }}>
        Client Programs
      </Typography>

      <Box className="cp-inner-box">
        <Box className="cp-pie-wrapper">
          <PieChart width={150} height={150}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={70}
              fill="#8884d8"
              dataKey="value"
              stroke="none"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>

          <Box className="cp-center-text">
            <Typography variant="h4" fontWeight="bold">
              {displayTotalClients}
            </Typography>
            <Typography variant="body2">Clients</Typography>
          </Box>
        </Box>

        <List className="cp-list">
          {listData.length > 0 ? listData.map((item, index) => (
            <ListItem 
              key={index} 
              className="cp-list-item"
              onClick={() => handleFilterClick(item.name)}
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { backgroundColor: 'rgba(87, 42, 241, 0.1)', color: '#572AF1' },
              }}
              title={`Filter clients by program: ${item.name}`}
            >
              <Box className="cp-dot" style={{ backgroundColor: item.color }} />
              <ListItemText
                primary={
                  <Typography component="span">
                    <strong>{item.value}</strong> {item.name}
                  </Typography>
                }
              />
            </ListItem>
          )) : (
            <ListItem>
              <ListItemText
                primary={
                  <Typography component="span" sx={{ color: '#666', fontStyle: 'italic' }}>
                    No programs available
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Box>
    </Box>
  );
};

export default ClientProgram;
